// src/pages/Settings.tsx
import React, { useState, useEffect } from 'react';
import { Save, Settings as SettingsIcon, AlertCircle } from 'lucide-react';
import { settingsService } from '../services/settings';
import { QueueControlStatus } from '../types/settings';

const Settings: React.FC = () => {
  const [batchSize, setBatchSize] = useState(256);
  const [threadCount, setThreadCount] = useState(16);
  const [currentValues, setCurrentValues] = useState<QueueControlStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [errors, setErrors] = useState<{ batchSize?: string; threadCount?: string }>({});

  useEffect(() => {
    loadCurrentSettings();
  }, []);

  const loadCurrentSettings = async () => {
    try {
      const data = await settingsService.getQueueStatus();
      setCurrentValues(data);
      setBatchSize(data.batchSize);
      setThreadCount(data.threadCount);
    } catch (error) {
      console.error('Failed to load settings:', error);
      showMessage('Failed to load current settings', 'error');
    }
  };

  const validateBatchSize = (value: number): string | undefined => {
    if (isNaN(value)) return 'Batch size must be a number';
    if (value < 16 || value > 2048) return 'Batch size must be between 16 and 2048';
    return undefined;
  };

  const validateThreadCount = (value: number): string | undefined => {
    if (isNaN(value)) return 'Thread count must be a number';
    if (value < 1 || value > 32) return 'Thread count must be between 1 and 32';
    return undefined;
  };

  const handleSave = async () => {
    const batchError = validateBatchSize(batchSize);
    const threadError = validateThreadCount(threadCount);

    if (batchError || threadError) {
      setErrors({ batchSize: batchError, threadCount: threadError });
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      await settingsService.updateBatchSize(batchSize);
      await settingsService.updateThreadCount(threadCount);
      showMessage('Settings saved successfully. Changes will apply on next processing cycle.', 'success');
      await loadCurrentSettings();
    } catch (error: any) {
      showMessage(error.response?.data?.error || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text: string, type: 'success' | 'error' | 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Performance Settings</h1>
        <p className="mt-2 text-sm text-gray-600">
          Configure system performance and resource utilization
        </p>
      </div>

      {message && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : message.type === 'error'
              ? 'bg-red-50 text-red-800 border border-red-200'
              : 'bg-blue-50 text-blue-800 border border-blue-200'
          }`}
        >
          {message.type === 'error' && <AlertCircle size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-6 pb-4 border-b">
          <SettingsIcon size={20} className="text-gray-700" />
          <h2 className="text-xl font-semibold">Performance Tuning</h2>
        </div>

        <div className="space-y-6">
          {/* Batch Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SQL Batch Size
            </label>
            <input
              type="number"
              min="16"
              max="2048"
              step="16"
              value={batchSize}
              onChange={(e) => {
                setBatchSize(parseInt(e.target.value));
                setErrors({ ...errors, batchSize: undefined });
              }}
              className={`w-full max-w-xs px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.batchSize ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <p className="text-sm text-gray-600 mt-1">
              Number of emails to fetch per SQL batch (16-2048). Larger batches improve throughput
              but use more memory.
            </p>
            {currentValues && (
              <p className="text-sm text-gray-500 mt-1">
                Current value: <span className="font-medium">{currentValues.batchSize}</span>
              </p>
            )}
            {errors.batchSize && (
              <p className="text-sm text-red-600 mt-1">{errors.batchSize}</p>
            )}
          </div>

          {/* Thread Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Processing Threads
            </label>
            <input
              type="number"
              min="1"
              max="32"
              value={threadCount}
              onChange={(e) => {
                setThreadCount(parseInt(e.target.value));
                setErrors({ ...errors, threadCount: undefined });
              }}
              className={`w-full max-w-xs px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.threadCount ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <p className="text-sm text-gray-600 mt-1">
              Number of parallel threads for sending emails (1-32). More threads increase throughput
              but require more system resources.
            </p>
            {currentValues && (
              <p className="text-sm text-gray-500 mt-1">
                Current value: <span className="font-medium">{currentValues.threadCount}</span>
              </p>
            )}
            {errors.threadCount && (
              <p className="text-sm text-red-600 mt-1">{errors.threadCount}</p>
            )}
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
